/**
 * YAML Format Tool
 *
 * Format/prettify YAML with configurable indentation.
 */

import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const YamlFormatTool: ITransformerTool = {
  id: 'yaml-format',
  name: 'YAML Format',
  description: 'Format and prettify YAML with configurable indentation',
  version: '1.0.0',
  type: 'transformer',
  category: 'Formatters & Minifiers',
  keywords: ['yaml', 'format', 'beautify', 'prettify', 'indent'],

  seo: {
    slug: 'yaml-formatter',
    pageTitle: 'YAML Formatter - Free Online Tool | SWE DevTools',
    metaDescription: 'Format and prettify YAML with configurable indentation. Clean up messy YAML files instantly. Fast, private, runs entirely in your browser.',
    keywords: ['yaml formatter', 'yaml beautifier', 'format yaml online', 'yaml prettify', 'yaml indent'],
    relatedTools: ['yaml-minify', 'yaml-validate', 'yaml-to-json'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.YAML, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.YAML,

  attributes: [
    {
      name: 'indent',
      type: 'number',
      label: 'Indent Size',
      defaultValue: 2,
      description: 'Number of spaces per indentation level',
      validation: { min: 1, max: 8, step: 1 },
    },
    {
      name: 'sortKeys',
      type: 'boolean',
      label: 'Sort Keys',
      defaultValue: false,
      description: 'Sort object keys alphabetically',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const indent = typeof config.indent === 'number' ? config.indent : 2;
    const sortKeys = config.sortKeys === true;
    const text = String(input ?? '').trim();
    if (!text) return '';

    try {
      const parsed = parseYaml(text);
      return stringifyYaml(parsed, { indent, sortMapEntries: sortKeys }).trimEnd();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to format YAML';
      throw ToolError.execution(message);
    }
  },
};
